import type { Error as DomainError } from '@/lib/api/generated/model';

/**
 * Why defining a label was refused.
 *
 * Two kinds, because only one of them is the reader's to act on: a name already
 * in the vocabulary is answered by typing a different one, and everything else
 * is answered by trying again. A single "it failed" message would leave someone
 * retyping the same colliding word.
 */
export type CreateLabelRefusal =
  | { kind: 'name-taken'; message: string }
  | { kind: 'unknown'; message: string };

/**
 * The backend's global error handler emits the stable domain-error envelope
 * `{ error: "<CODE>", message?: string }` for every refusal, so the branch is
 * on `error` rather than on the status alone — the same rule `features/tasks`
 * follows for a refused move.
 *
 * The service's own sentence is deliberately not shown. It names the workspace
 * and quotes the stored name back ("Workspace already has a label named
 * 'acme'."), which is a server's account of the conflict rather than the one
 * thing the person in front of the picker needs: that word is taken, pick
 * another.
 */
export function classifyCreateLabelFailure(
  body: DomainError | undefined,
): CreateLabelRefusal {
  return classifyLabelFailure(body, 'created');
}

/**
 * The same classification for a rename.
 *
 * Split by the *verb* rather than duplicated, because only the fallback differs:
 * a taken name reads identically whichever act hit it, but "The label could not
 * be created." shown after pressing Save on a rename describes an act nobody
 * performed. A 404 — someone else deleted the label while the editor was open —
 * lands here, and that is exactly when a reader needs the sentence to be about
 * the thing they actually did.
 */
export function classifyUpdateLabelFailure(
  body: DomainError | undefined,
): CreateLabelRefusal {
  return classifyLabelFailure(body, 'updated');
}

function classifyLabelFailure(
  body: DomainError | undefined,
  verb: 'created' | 'updated',
): CreateLabelRefusal {
  if (body?.error === 'LABEL_NAME_CONFLICT') {
    return {
      kind: 'name-taken',
      message: 'A label with this name already exists.',
    };
  }
  return {
    kind: 'unknown',
    message: `The label could not be ${verb}.`,
  };
}

/**
 * A refused label write, as a rejection.
 *
 * The picker's `onCreate` answers with the created label, so a refusal has to
 * arrive as a rejected promise — and it carries the classified refusal rather
 * than only a string, so a caller that wants to treat a taken name differently
 * from a failed request can, without re-parsing a message.
 */
export class CreateLabelError extends Error {
  readonly refusal: CreateLabelRefusal;

  constructor(refusal: CreateLabelRefusal) {
    super(refusal.message);
    this.name = 'CreateLabelError';
    this.refusal = refusal;
  }
}
